<template>
  <date-picker
    type="datetime"
    v-model:value="value"
    class="date-time-picker"
    :format="hideTime ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'"
    :editable="false"
    :class="{
      'date-time-picker--required': required,
    }"
    :show-time-panel="false"
    :lang="lang"
    :disabled="disabled"
    :disabled-date="disabledDate"
    :append-to-body="appendToBody"
    @pick="pick"
  >
    <template #input="inputProps">
      <div
        class="v-field"
        :class="{
          'v-field--error': error,
          'v-field--disabled': disabled,
          'v-field--active': focused || modelValue
        }"
      >
        <template v-if="label">
          <v-field-label floating :for="inputId">{{ $t(label) }}</v-field-label>
          <v-field-label :for="inputId">{{ $t(label) }}</v-field-label>
        </template>
        <input :id="inputId" v-bind="inputProps" v-mask="'####-##-## ##:##'" @focusin="focused = true" @focusout="focused = false" />
        <div class="v-field__outline"></div>
      </div>
      <div class="date-time-picker__error" v-if="error">{{ $t(error) }}</div>
    </template>
    <template #header="headerProps">
      <div class="date-time-picker__header d-flex flex-column ga-2">
        <Input v-mask="'####-##-##'" :placeholder="$t('ГГГГ-ММ-ДД')" :model-value="date" @update:model-value="onUpdateDate($event, headerProps)" autofocus />
        <Input v-if="!hideTime" v-mask="'##:##'" placeholder="00:00" :model-value="time" @update:model-value="onUpdateTime($event, headerProps)" />
      </div>
    </template>
    <template #footer="{ emit }" v-if="hasValidDate">
      <button type="button" class="mx-btn mx-btn-text" @click="emit()">{{ $t('Очистить') }}</button>
      <button type="button" class="mx-btn mx-btn-text" @click="emit(new Date(value))">{{ $t('Выбрать') }}</button>
    </template>
  </date-picker>
</template>

<script>
import { useId } from 'vue';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import 'vue-datepicker-next/locale/ru';
import Input from './Input';

export default {
  name: 'DateTimePicker',
  components: {
    DatePicker,
    Input,
  },
  props: {
    id: String,
    modelValue: Date,
    label: String,
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledDate: {
      type: Function,
      default: () => false,
    },
    error: String,
    appendToBody: {
      type: Boolean,
      default: true
    },
    // @todo удалить props и из компонентов, что его используют, если не будет использоваться пол года
    deafaultNow: {
      type: Boolean,
      default: false
    },
    hideTime: {
      type: Boolean,
      default: false
    },
    lang: 'ru'
  },
  emits: ['update:modelValue'],
  data() {
    return {
      uid: useId(),
      value: this.modelValue,
      focused: false,
      date: '',
      time: '',
      setCurrentTime: false,
    };
  },
  methods: {
    pick() {
      this.setCurrentTime = true;
    },
    refreshDateAndTime() {
      if (!this.hasValidDate) {
        this.date = '';
        this.time = '';
        return;
      }
      const dt = this.value;
      this.date = `${dt.getFullYear()}-${('0' + (dt.getMonth() + 1)).slice(-2)}-${('0' + dt.getDate()).slice(-2)}`;
      this.time = `${('0' + dt.getHours()).slice(-2)}:${('0' + dt.getMinutes()).slice(-2)}`;
    },
    onUpdateDate(date) {
      const time = !this.time.length ? '00:00' : this.time;
      this.validateDatetime(date, time);
    },
    onUpdateTime(time) {
      this.validateDatetime(this.date, time);
    },
    validateDatetime(date, time) {
      if (date.length !== 10 || time.length !== 5) {
        return;
      }
      const datetime = new Date(`${date}T${time}`);
      if (isNaN(datetime) || this.disabledDate(datetime)) {
        return;
      }
      this.value = datetime;
    },
    isValidDate(date) {
      return date && !isNaN(date);
    }
  },
  computed: {
    inputId() {
      return this.id || `input-${this.uid}`;
    },
    hasValidDate() {
      return this.isValidDate(this.value);
    }
  },
  watch: {
    modelValue(value) {
      this.value = value;
      this.refreshDateAndTime(value);
    },
    value(value, oldValue) {
      if (this.isValidDate(value) && !this.isValidDate(oldValue) && this.setCurrentTime) {
        var now = new Date();
        value.setHours(now.getHours());
        value.setMinutes(now.getMinutes());
        this.setCurrentTime = false;
      }

      this.refreshDateAndTime(value);
      this.$emit('update:modelValue', value);
    },
  },
  mounted() {
    this.value = this.modelValue;
    this.refreshDateAndTime(this.value);
  }
};
</script>

<style lang="scss">
.date-time-picker {
  --v-input-padding-top: 16px;
  width: 350px;

  .mx-input {
    width: auto;
    height: 52px;
    padding: 24px 16px 4px;
    font-size: 16px;
    border: none;
    box-shadow: none;
  }

  .mx-icon-calendar, .mx-icon-clear {
    top: 27px
  }

  &--required .v-field-label::after {
    content: ' *';
    color: rgba(var(--error-color));
  }

  &__error {
    margin-top: 4px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(var(--error-color));
  }

  .v-field-label--floating {
    top: 7px;
  }

  .v-field__outline {
    top: 0;
    border: 1px solid rgb(var(--border-color));
    border-radius: 8px;
    color: rgb(var(--boulder-color));
  }

  .v-field--error:not(.v-field--disabled) .v-field__outline {
    border-color: rgba(var(--error-color));
  }

  &__header {
    position: relative;
    color: #ffffff;

    &-timezone {
      position: absolute;
      left: 68px;
      bottom: 7px;
      color: var(--grey-1);
    }

    .v-input {
      input {
        padding: 6px 16px;
        min-height: 34px;
      }

      &__details {
        display: none;
      }

      .v-field__outline {
        display: none;
      }
    }
  }
}
</style>